import { faListCheck } from '@fortawesome/pro-regular-svg-icons/faListCheck';
import { faArrowRightArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowRightArrowLeft';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WorkflowStepAction } from '@spike/masterdata-model';
import clsx from 'clsx';
import { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import { BulkActionsTooltip } from './BulkActionsTooltip';
import BulkStatusMenu from './BulkStatusMenu';
import styles from './BulkActionsToolbar.module.scss';

interface BulkActionsToolbarProps {
    selectedCount: number;
    actions: Array<WorkflowStepAction>;
    /** Id of the scrollable list container the toolbar should stay horizontally centered on. */
    scrollerContainerId?: string;
    className?: string;
    moveMenu?: ReactNode;
    onSelectStatus: (statusId: number, statusName: string) => void;
    onMove?: () => void;
    onClear?: () => void;
}

export const BulkActionsToolbar: FunctionComponent<BulkActionsToolbarProps> = props => {
    const [expanded, setExpanded] = useState(false);
    const [left, setLeft] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const pillRef = useRef<HTMLDivElement>(null);
    const iconRef = useRef<HTMLDivElement>(null);

    const hasActions = props.actions.length > 0;

    useEffect(() => {
        // Note: we intentionally never apply a CSS `transform` to the fixed-position
        // container below (e.g. `translateX(-50%)` for centering). A `transform` on an
        // ancestor creates a new containing block for `position: fixed` descendants
        // (like the Tooltip content), which would make them position themselves relative
        // to this container instead of the viewport and render off-screen. Instead we
        // measure the container's own width and compute the centered `left` in JS.
        const updatePosition = () => {
            const scroller = props.scrollerContainerId ? document.querySelector(`#${props.scrollerContainerId}`) : null;

            if (scroller && containerRef.current) {
                const scrollerRect = scroller.getBoundingClientRect();
                const centerX = scrollerRect.left + scrollerRect.width / 2;
                setLeft(centerX - containerRef.current.offsetWidth / 2);
            }
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);

        const resizeObserver = new ResizeObserver(updatePosition);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', updatePosition);
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.scrollerContainerId]);

    const toggleExpanded = () => {
        if (hasActions) {
            setExpanded(prev => !prev);
        }
    };

    const selectStatusHandler = (statusId: number, statusName: string) => {
        setExpanded(false);
        props.onSelectStatus(statusId, statusName);
    };

    return (
        <div
            ref={containerRef}
            className={clsx(styles.container, { [styles.containerVisible]: left !== null }, props.className)}
            style={left !== null ? { left } : undefined}
        >
            {expanded && (
                <BulkStatusMenu
                    actions={props.actions}
                    onClick={selectStatusHandler}
                    onClose={() => setExpanded(false)}
                    ignoreRef={pillRef}
                />
            )}
            {props.moveMenu}
            <div
                ref={pillRef}
                className={clsx(styles.pill, { [styles.pillDisabled]: !hasActions && !props.onMove })}
                onClick={toggleExpanded}
            >
                <span className={styles.label}>
                    {props.selectedCount} booking{props.selectedCount === 1 ? '' : 's'} selected
                </span>
                <div className={styles.actions}>
                    {props.onMove && (
                        <BulkActionsTooltip
                            content="Move pets between rooms"
                            anchorRef={pillRef}
                        >
                            <div
                                className={styles.iconButton}
                                onClick={event => {
                                    event.stopPropagation();
                                    setExpanded(false);
                                    props.onMove?.();
                                }}
                            >
                                <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                            </div>
                        </BulkActionsTooltip>
                    )}
                    <BulkActionsTooltip
                        content="Change status"
                        disabled={expanded || !hasActions}
                        anchorRef={pillRef}
                        alignRef={iconRef}
                    >
                        <div
                            className={styles.iconButton}
                            ref={iconRef}
                        >
                            <FontAwesomeIcon icon={faListCheck} />
                        </div>
                    </BulkActionsTooltip>
                    {props.onClear && (
                        <div
                            className={styles.iconButton}
                            aria-label="Deselect all"
                            onClick={event => {
                                event.stopPropagation();
                                setExpanded(false);
                                props.onClear?.();
                            }}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkActionsToolbar;
